<template>
  <div class="user-edit-modal">
    <modal-frame :showModalFlag="modalFrameDisplay">
      <div class="modal-header">
        <h1>アカウント情報編集</h1>
      </div>
      <div class="modal-body">
        <attribute-in-form
          :labelText="'ログインID'"
          :necessary="true"
          :validationState="validateAccountNameResult.state"
          :validationMessage="validateAccountNameResult.message"
          :attributeNotify="'6文字以上120文字以下の半角英数字(大文字小文字)と記号!@._/-+()が利用可能,前後の空白は削除されます'">
          <input type="text" size="30" v-model="editUser.account_name" />
        </attribute-in-form>
        <attribute-in-form
          :labelText="'表示名'"
          :necessary="true"
          :validationState="validateName.state"
          :validationMessage="validateName.message"
          :attributeNotify="'20文字以内,前後の空白は削除されます'">
          <input type="text" size="30" v-model="editUser.name" />
        </attribute-in-form>
        <attribute-in-form
          :labelText="'詳細'"
          :necessary="false"
          :validationState="validateDescription.state"
          :validationMessage="validateDescription.message"
          :attributeNotify="'0文字以上、126文字以内,前後の空白は削除されます'">
          <textarea class="description-input" v-model="editUser.description"></textarea>
        </attribute-in-form>
        <attribute-in-form
          :labelText="'メールアドレス'"
          :necessary="true"
          :validationState="validateMail.state"
          :validationMessage="validateMail.message"
          :attributeNotify="'変更の場合はメール認証トークンが必要です。前後の空白は削除されます'">
          <input type="text" size="30" v-model="editUser.email" />
        </attribute-in-form>
        <attribute-in-form
          :labelText="'メール認証トークン'"
          :necessary="false"
          :validationState="validateToken.state"
          :validationMessage="validateToken.message"
          :attributeNotify="'半角数字8文字,前後の空白は削除されます'">
          <input type="text" size="12" class="token-input" v-model="mailValidateToken" />
          <button class="send-validation-btn" :class="{enable: validateMail.valid && validateName.valid}" @click="sendMailValidation">認証メールを送信</button>
        </attribute-in-form>
        <attribute-in-form
          :labelText="'ポートレート'"
          :necessary="false"
          :validationState="'none'"
          :validationMessage="''"
          :attributeNotify="''">
          <div class="portrate-setting-column">
            <div class="portrate-thumbnail-column" v-if="user">
              <user-portrate :target="user" :displaySize="'middle'"/>
              <div class="caption">現在設定されているアイコン</div>
            </div>
            <mono-photo-selector
              :photoObj="portrateObj"
              v-on:photoSelectedEvent="photoSelected"
              v-on:cancelSelectPhotoEvent="selectedPhotoCancel"
              />
          </div>
        </attribute-in-form>
        <attribute-in-form
          :labelText="'テーマカラー'"
          :necessary="false"
          :validationState="'none'"
          :validationMessage="''"
          :attributeNotify="'ポートレート画像を表示しなかったときに自動生成されるアイコンの背景色です'">
          <input type="color" class="theme-color-input" v-model="editUser.theme_color" />
        </attribute-in-form>
        <div class="form-controll">
          <button class="cancel-btn" @click="cancelSubmit">キャンセル</button>
          <button class="submit-btn" :class="{ disable: !isValid }" @click="submitForm">更新</button>
        </div>
      </div>
    </modal-frame>
    <confirm-dialog
      :showDialog="confirmDialogVisible"
      :title="confirmDialogTitle"
      :message="confirmDialogMessage"
      :dialogType="confirmDialogType"
      v-on:negativeResultEvent="confirmFinishNegative"
      v-on:positiveResultEvent="confirmFinishPositive"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import ModalFrame from '@/components/common/ModalFrame.vue'
import AttributeInForm from '@/components/common/AttributeInForm.vue'
import MonoPhotoSelector from '@/components/appView/common/MonoPhotoSelector.vue'
import UserPortrate from '@/components/appView/common/UserPortrate.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

import ConfirmDialogControll from '@/mixin/ConfirmDialogControll.js'

import * as DIALOG_TYPE from '@/assets/constants/dialogType.js'

export default {
  mixins: [ConfirmDialogControll],

  name: 'UserEditModal',

  components: {
    ModalFrame,
    AttributeInForm,
    MonoPhotoSelector,
    UserPortrate,
    ConfirmDialog,
  },

  props: {
    user: Object,
    modalFrameDisplay: Boolean
  },

  emits: [
    'cancelEditEvent',
    'submitEditEvent',
  ],

  data () {
    return {
      editUser: {},
      portrateObj: null,
      mailValidateToken: '',
      validateAccountNameResult: {}
    }
  },

  watch: {
    modalFrameDisplay: { handler: 'initObject', immediate: true },
    'editUser.account_name': { handler: 'validateAccountName', immediate: true },
  },

  computed: {
    validateName () {
      const segmenter = new Intl.Segmenter("ja-JP", { granularity: "grapheme"})
      if (this.editUser.name && this.editUser.name.trim().length > 0 && [...segmenter.segment(this.editUser.name.trim())].length <= 20) {
        return { state: 'valid', valid: true, message: '' }
      } else {
        return { state: 'invalid', valid: false, message: '入力必須でかつ、20文字以下です' }
      }
    },

    validateDescription () {
      const segmenter = new Intl.Segmenter("ja-JP", { granularity: "grapheme"})
      if (this.editUser.description !== null && [...segmenter.segment(this.editUser.description.trim())].length <= 126) {
        return { state: 'valid', valid: true, message: '' }
      } else {
        return { state: 'invalid', valid: false, message: '126文字以下である必要があります' }
      }
    },

    descriptionStringCount () {
      const segmenter = new Intl.Segmenter("ja-JP", { granularity: "grapheme"})
      return [...segmenter.segment(this.editUser.description)].length
    },

    validateMail () {
      if (!this.editUser.email || this.editUser.email.trim().length <= 0 ) {
        return { state: 'invalid', valid: false, message: 'メールアドレスの入力は必須です' }
      } else if (this.editUser.email.match(/.+@.+\..+/)) {
        return { state: 'valid', valid: true, message: '' }
      } else {
        return { state: 'invalid', valid: false, message: 'メールアドレスの形式が正しくありません' }
      }
    },

    validateToken () {
      if (!this.mailValidateToken || this.mailValidateToken.trim().length <= 0 ) {
        return { state: 'none', valid: true, message: '' }
      } else if (this.mailValidateToken.trim().match(/^\d{8,8}$/g)) {
        return { state: 'valid', valid: true, message: '' }
      } else {
        return { state: 'invalid', valid: false, message: '形式が正しくありません。半角数字8桁の値を入力してください。' }
      }
    },

    isValid () {
      if (
        this.validateAccountNameResult.valid 
        && this.validateName.valid 
        && this.validateDescription.valid
        && this.validateMail.valid 
        && this.validateToken.valid
      ) {
        return true
      }
      return false
    }
  },

  methods: {
    ...mapActions({
      userNameAvailable: 'user/nameAvailable',
      userSendMailValidation: 'user/sendMailValidation',
    }),

    initObject () {
      if (this.user) {
        this.editUser.name          = this.user.name
        this.editUser.account_name  = this.user.account_name
        this.editUser.email         = this.user.email
        this.editUser.description   = this.user.description
        this.editUser.icon_img_path = this.user.icon_img_path
        this.editUser.theme_color   = this.user.theme_color
        this.portrateObj = null
        this.mailValidateToken = ''
        this.validateAccountName()
      } else {
        this.editUser = {}
        this.portrateObj = null
        this.mailValidateToken = ''
      }
    },

    async validateAccountName () {
      console.log(this.editUser.account_name)
      if (this.editUser.account_name) {
        console.log('account_name exist')
        if (this.editUser.account_name === this.user.account_name) {
          console.log('account_name not change')
          this.validateAccountNameResult = { state: 'valid', valid: true, message: '' }
        } else if (!this.editUser.account_name.trim().match(/^([a-zA-Z0-9]|!|@|\.|_|\/|-|\+|\(|\)){6,120}$/) ) {
          console.log('account_name not match regex')
          this.validateAccountNameResult = { state: 'invalid', valid: false, message: 'ログインIDの形式が正しくありません'}
        } else {
          console.log('account_name try to exist')
          try {
            let accountNameAvailable = await this.userNameAvailable(this.editUser.account_name.trim())
            console.log(accountNameAvailable)
            if (accountNameAvailable.available) {
              this.validateAccountNameResult = { state: 'valid', valid: true, message: '' }
            } else {
              if (accountNameAvailable.message === 'ALREADY USED') {
                this.validateAccountNameResult = { state: 'invalid', valid: false, message: 'このIDは利用できません。'}
              } else {
                this.validateAccountNameResult = { state: 'invalid', valid: false, message: 'ログインIDの形式が正しくありません' }
              }
            }
          } catch (error) {
            console.error(error)
          }
        }
      } else {
        this.validateAccountNameResult = { state: 'invalid', valid: false, message: 'ログインIDは入力必須です' }
      }
    },

    sendMailValidation () {
      if (this.validateName.valid && this.validateMail.valid) {
        this.userSendMailValidation({ name: this.editUser.name, email: this.editUser.email, userID: this.user.id }).then(() => {
          this.showConfirmDialog(
            '認証メールを送信しました',
            'メールアドレスの認証のために、' + this.editUser.email + '宛にメールを送信しました。\nメール本文に書かれている8桁の認証番号を入力してください。',
            DIALOG_TYPE.CONFIRM,
            () => {}
          )
        }).catch(err => {
          console.error(err)
          this.showNotifyDialog('メールの送信に失敗しました', '認証メールを送信しようとしましたが、失敗しました。')
        })
      // } else {
      //   this.showNotifyDialog('メールを送信できません', '認証メール送信には名前とメールアドレスが必要です')
      }
    },

    photoSelected (photoObj) {
      this.portrateObj = photoObj
    },

    selectedPhotoCancel () {
      this.portrateObj = null
    },

    cancelSubmit () {
      this.$emit('cancelEditEvent')
    },

    submitForm () {
      if (this.isValid) {
        let formData = new FormData()
        formData.append('user[name]', this.editUser.name.trim())
        if (this.editUser.account_name && this.editUser.account_name !== this.user.account_name) {
          formData.append('user[account_name]', this.editUser.account_name.trim())
        }
        if (this.editUser.email.length > 0 && this.mailValidateToken.length > 0 && this.editUser.email !== this.user.email) {
          formData.append('mail_validate_token', this.mailValidateToken.trim())
          formData.append('user[email]', this.editUser.email.trim())
        }
        formData.append('user[description]', this.editUser.description.trim())
        if (this.portrateObj) {
          formData.append('user[icon_img]', this.portrateObj.uploadFile, this.portrateObj.name)
        }
        formData.append('user[theme_color]', this.editUser.theme_color)
        console.log('gonna fire event.' + formData)
        this.$emit('submitEditEvent', formData)
      }
    },

  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/global.scss';
@import '@/assets/css/mediaQuery.scss';
@import '@/assets/css/colorTable.scss';

.user-edit-modal {
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    padding-bottom: 0;
    h1 {
      margin: 0;
      font-size: 22pt;
      font-weight: normal;
    }
  }
  .modal-body {
    padding: 20px;
    input[type="text"] {
      font-size: 20px;
      width: calc(100% - 60px);
      border-radius: 5px;
      &.token-input {
        width: 100px;
      }
    }
    .description-input {
      font-size: 20px;
      height: 3rem;
      border-radius: 5px;
      width: calc(100% - 60px);
    }
    .portrate-setting-column {
      width: calc(100% - 60px);
    }
    .portrate-thumbnail-column {
      padding: 5px;
      border: 1px solid #ddd;
      text-align: center;
      .caption {
        margin-top: 5px;
        font-size: 0.8rem;
      }
    }
    .send-validation-btn {
      margin-left: 20px;
      border-radius: 5px;
      border: 1px solid $disable-btn-color;
      background-color: $disable-btn-color;
      color: $normal-btn-text-color;
      &.enable {
        border: 1px solid $normal-btn-color;
        background-color: $normal-btn-color;
        color: $normal-btn-text-color;
      }
    }
  }

}
</style>